import React, { useState, useEffect, useMemo, useRef, useCallback, useImperativeHandle } from 'react';
import { ChevronRight, ChevronDown, Workflow, MoreVertical } from 'lucide-react';
import axios from 'axios';
import logger from '../../utils/logger';
import projectService from '../../services/projectService';
import { useAuth } from '../../AuthProvider';
import { canFinalize } from '../../utils/roles';
import {
  Button,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  TooltipProvider,
  Tooltip,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui';
import SetWorkflowModal from '../modals/SetWorkflowModal';
import YoutubeLinkCard from '../synopsis/detail/YoutubeLinkCard';
import OverviewCard from '../synopsis/detail/OverviewCard';
import LeaveNoteCard from '../synopsis/detail/LeaveNoteCard';
import TranscriptionReadOnlyBox, { computeSegmentMissingInOther } from '../keyword/detail/TranscriptionReadOnlyBox';
import EditableTranscriptBox from '../keyword/detail/EditableTranscriptBox';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

function getInitial(name) {
  if (!name || name === '—') return '—';
  const s = String(name).trim();
  return (s[0] || '—').toUpperCase();
}

function formatSegmentTime(seconds) {
  const s = Number(seconds) || 0;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const t = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${t.toFixed(3)}`;
}

function segmentsToVtt(segments) {
  if (!Array.isArray(segments) || segments.length === 0) return '';
  const lines = ['WEBVTT', ''];
  segments.forEach((seg, i) => {
    const start = formatSegmentTime(seg.start);
    const end = formatSegmentTime(seg.end);
    lines.push(`${i + 1}`);
    lines.push(`${start} --> ${end}`);
    lines.push(seg.text || '');
    lines.push('');
  });
  return lines.join('\n');
}

function formatSegmentTimeSrt(seconds) {
  const s = Number(seconds) || 0;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const t = s % 60;
  const ms = Math.round((t % 1) * 1000);
  const sec = Math.floor(t);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')},${String(ms).padStart(3, '0')}`;
}

function segmentsToSrt(segments) {
  if (!Array.isArray(segments) || segments.length === 0) return '';
  const lines = [];
  segments.forEach((seg, i) => {
    const start = formatSegmentTimeSrt(seg.start);
    const end = formatSegmentTimeSrt(seg.end);
    lines.push(String(i + 1));
    lines.push(`${start} --> ${end}`);
    lines.push(seg.text || '');
    lines.push('');
  });
  return lines.join('\n');
}

const VideoAndTimestampEditor = React.forwardRef(function VideoAndTimestampEditor({ videoUrl, vttBlobUrl, onTimeUpdate }, ref) {
  const videoRef = useRef(null);
  const stopAtCleanupRef = useRef(null);

  const seekTo = useCallback((start, end) => {
    const video = videoRef.current;
    if (!video) return;
    if (stopAtCleanupRef.current) {
      stopAtCleanupRef.current();
      stopAtCleanupRef.current = null;
    }
    const startNum = Number(start);
    if (!Number.isFinite(startNum)) return;
    video.currentTime = startNum;
    video.play().catch(() => {});
    const endNum = end !== undefined && end !== null ? Number(end) : NaN;
    if (!Number.isFinite(endNum) || endNum <= startNum) return;
    const stopAt = endNum;
    let intervalId = null;
    let seekedTimeoutId = null;
    const cleanup = () => {
      if (!stopAtCleanupRef.current) return;
      stopAtCleanupRef.current = null;
      if (seekedTimeoutId != null) clearTimeout(seekedTimeoutId);
      video.removeEventListener('pause', cleanup);
      video.removeEventListener('seeked', onSeekedCleanup);
      if (intervalId != null) clearInterval(intervalId);
    };
    const onSeekedCleanup = () => { cleanup(); };
    intervalId = setInterval(() => {
      if (!videoRef.current) return;
      const t = videoRef.current.currentTime;
      if (t >= stopAt) {
        videoRef.current.pause();
        cleanup();
      }
    }, 80);
    stopAtCleanupRef.current = cleanup;
    video.addEventListener('pause', cleanup);
    // Delay adding seeked so we don't react to our own seek (which fires seeked and would clear the interval)
    seekedTimeoutId = setTimeout(() => {
      seekedTimeoutId = null;
      if (stopAtCleanupRef.current === cleanup) {
        video.addEventListener('seeked', onSeekedCleanup);
      }
    }, 600);
  }, []);

  useImperativeHandle(ref, () => ({ seekTo }), [seekTo]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleTimeUpdate = () => {
      if (onTimeUpdate) onTimeUpdate(video.currentTime);
    };
    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, [onTimeUpdate]);

  return (
    <div className="rounded-sm rounded-b-none bg-card shadow-subtle-sm overflow-hidden">
      <div className="p-0">
        <div className="w-full overflow-hidden bg-black">
          <video
            ref={videoRef}
            src={videoUrl}
            controls
            className="w-full max-h-[280px] aspect-video object-contain"
            crossOrigin="anonymous"
          >
            <track src={vttBlobUrl} kind="subtitles" srcLang="en" default />
          </video>
        </div>
      </div>
    </div>
  );
});

const TranscriptionProjectDetailPage = ({ project, details, onClose, onDelete, onRefresh }) => {
  const { user } = useAuth();
  const canDeleteProject = user?.role === 'admin' || user?.app_metadata?.role === 'admin';
  const canApproveReject = user?.role === 'admin' || user?.role === 'pm' || user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'pm'; // PM/Admin for workflow
  const workflowStatus = project?.workflowStatus ?? project?.workflow_status ?? details?.project?.workflow_status;
  const workflowStatusDisplayName = project?.workflowStatusDisplayName ?? project?.workflow_status_display_name ?? details?.project?.workflow_status_display_name ?? '—';
  const transcriptDownloadCount = project?.transcript_download_count ?? details?.project?.transcript_download_count ?? 0;
  const lastTranscriptDownloadedAt = project?.last_transcript_downloaded_at ?? details?.project?.last_transcript_downloaded_at;
  const currentUserIsAssignee = details?.current_user_is_assignee === true;
  const canEditEditableTranscript = canApproveReject || (currentUserIsAssignee && workflowStatus !== 'ready_for_review'); // Assignee must accept first
  const showAcceptButton = workflowStatus === 'ready_for_review' && currentUserIsAssignee;
  const showSubmitButton = (workflowStatus === 'in_progress' || workflowStatus === 'rejected') && currentUserIsAssignee;
  const showApproveButton = workflowStatus === 'ready_for_pm_review' && canApproveReject;
  const showRejectButton = workflowStatus === 'ready_for_pm_review' && canApproveReject;
  const showDeliverButton = workflowStatus === 'pm_approved' && canApproveReject;
  const isDelivered = workflowStatus === 'delivered';
  const pmDisplayName = (details?.pm_display_name || '').trim() || 'PM';
  const pmStepStatusLabel = ['ready_for_pm_review', 'pm_approved', 'delivered'].includes(workflowStatus)
    ? workflowStatusDisplayName
    : 'Pending';
  const [workflowModalOpen, setWorkflowModalOpen] = useState(false);
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const canSetWorkflow = user?.role === 'admin' || user?.role === 'pm' || user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'pm';

  const fetchWorkflowSteps = useCallback(async () => {
    if (!project?.id) return;
    try {
      const data = await projectService.getWorkflowSteps(project.id);
      setWorkflowSteps(Array.isArray(data) ? data : []);
    } catch (e) {
      setWorkflowSteps([]);
    }
  }, [project?.id]);

  useEffect(() => {
    fetchWorkflowSteps();
  }, [fetchWorkflowSteps]);
  const [accepting, setAccepting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [delivering, setDelivering] = useState(false);
  const handleAccept = async () => {
    if (!project?.id || accepting) return;
    setAccepting(true);
    try {
      await projectService.updateWorkflowStatus(project.id, 'in_progress');
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Accept failed:', err);
      alert(err.response?.data?.detail || 'Failed to accept task');
    } finally {
      setAccepting(false);
    }
  };
  const handleSubmit = async () => {
    if (!project?.id || submitting) return;
    setSubmitting(true);
    try {
      await projectService.updateWorkflowStatus(project.id, 'ready_for_pm_review');
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Submit failed:', err);
      alert(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };
  const handleApprove = async () => {
    if (!project?.id || approving) return;
    setApproving(true);
    try {
      await projectService.updateWorkflowStatus(project.id, 'pm_approved');
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Approve failed:', err);
      alert(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setApproving(false);
    }
  };
  const handleReject = async () => {
    if (!project?.id || rejecting) return;
    if (!window.confirm('Reject this submission? It will go back to the reviewer.')) return;
    setRejecting(true);
    try {
      await projectService.updateWorkflowStatus(project.id, 'rejected');
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Reject failed:', err);
      alert(err.response?.data?.detail || 'Failed to reject');
    } finally {
      setRejecting(false);
    }
  };
  const handleDeliver = async () => {
    if (!project?.id || delivering) return;
    setDelivering(true);
    try {
      await projectService.updateWorkflowStatus(project.id, 'delivered');
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Deliver failed:', err);
      alert(err.response?.data?.detail || 'Failed to deliver');
    } finally {
      setDelivering(false);
    }
  };
  const metadata = details?.metadata || project?.metadata;
  const keywords = details?.keywords || project?.keywords;
  const transcript = details?.transcript || project?.transcript;
  const [showUseForEditingConfirmModal, setShowUseForEditingConfirmModal] = useState(false);

  const googleSttTranscript = transcript;
  const whisperTranscript = details?.transcript_whisper ?? project?.transcript_whisper ?? null;

  const rawFinalEditable = details?.final_editable_transcript ?? details?.project?.final_editable_transcript ?? project?.final_editable_transcript;
  const finalEditableTranscript = (() => {
    if (rawFinalEditable == null) return null;
    let obj = rawFinalEditable;
    if (typeof rawFinalEditable === 'string' && rawFinalEditable.trim()) {
      try {
        const parsed = JSON.parse(rawFinalEditable);
        if (parsed && typeof parsed === 'object') obj = parsed;
      } catch (_) { /* leave obj as string */ }
    }
    if (typeof obj === 'object' && obj !== null && 'transcript_text' in obj) {
      const src = obj.source;
      return {
        transcript_text: obj.transcript_text ?? '',
        transcript_segments: Array.isArray(obj.transcript_segments) ? obj.transcript_segments : undefined,
        source: src === 'whisper' || src === 'google_stt' ? src : undefined,
      };
    }
    return { transcript_text: String(rawFinalEditable), transcript_segments: undefined, source: undefined };
  })();

  const [editableColumnSource, setEditableColumnSource] = useState('google_stt');
  const [showEditableSourceDropdown, setShowEditableSourceDropdown] = useState(false);
  const [editableColumnChoosingOther, setEditableColumnChoosingOther] = useState(false);
  const [isUseForEditingLoading, setIsUseForEditingLoading] = useState(false);
  const [deliveredExportFormat, setDeliveredExportFormat] = useState('vtt');

  useEffect(() => {
    if (finalEditableTranscript?.source === 'whisper' || finalEditableTranscript?.source === 'google_stt') {
      setEditableColumnSource(finalEditableTranscript.source);
    }
  }, [finalEditableTranscript?.source]);

  const [playableMediaUrl, setPlayableMediaUrl] = useState(null);
  const [playableMediaType, setPlayableMediaType] = useState(null);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const videoEditorRef = useRef(null);

  const handleSegmentSeek = useCallback((start, end) => {
    videoEditorRef.current?.seekTo?.(start, end);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!project?.id) return;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/projects/${project.id}/media-playable-url`, { withCredentials: true });
        if (!cancelled && res.data?.url) {
          setPlayableMediaUrl(res.data.url);
          setPlayableMediaType(res.data.type || null);
        } else if (!cancelled) {
          setPlayableMediaUrl(null);
          setPlayableMediaType(null);
        }
      } catch (e) {
        if (!cancelled) {
          setPlayableMediaUrl(null);
          setPlayableMediaType(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [project?.id]);

  const segmentsForEditor = useMemo(() => {
    const t = details?.transcript || project?.transcript;
    const tw = details?.transcript_whisper ?? project?.transcript_whisper;
    const segs = t?.transcript_segments ?? tw?.transcript_segments;
    return Array.isArray(segs) && segs.length > 0 ? segs : null;
  }, [details?.transcript, details?.transcript_whisper, project?.transcript, project?.transcript_whisper]);

  const { googleMissingInWhisper, whisperMissingInGoogle } = useMemo(() => {
    const g = Array.isArray(googleSttTranscript?.transcript_segments) ? googleSttTranscript.transcript_segments : [];
    const w = Array.isArray(whisperTranscript?.transcript_segments) ? whisperTranscript.transcript_segments : [];
    return {
      googleMissingInWhisper: computeSegmentMissingInOther(g, w),
      whisperMissingInGoogle: computeSegmentMissingInOther(w, g),
    };
  }, [googleSttTranscript?.transcript_segments, whisperTranscript?.transcript_segments]);

  const vttBlobUrl = useMemo(() => {
    if (!segmentsForEditor) return null;
    const vtt = segmentsToVtt(segmentsForEditor);
    const blob = new Blob([vtt], { type: 'text/vtt' });
    return URL.createObjectURL(blob);
  }, [segmentsForEditor]);

  const vttBlobUrlRef = useRef(null);
  vttBlobUrlRef.current = vttBlobUrl;
  useEffect(() => {
    return () => {
      if (vttBlobUrlRef.current) {
        try {
          URL.revokeObjectURL(vttBlobUrlRef.current);
        } catch (_) {}
      }
    };
  }, []);

  const hasVideoSync = !!(playableMediaType === 'gcs' && playableMediaUrl && vttBlobUrl && segmentsForEditor);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showEditableSourceDropdown && !e.target.closest('.editable-column-source-dropdown')) setShowEditableSourceDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEditableSourceDropdown]);

  const handleUseForEditing = async () => {
    if (!project?.id || isUseForEditingLoading) return;
    if (finalEditableTranscript) {
      setShowUseForEditingConfirmModal(true);
      return;
    }
    await applyUseForEditing();
  };

  const applyUseForEditing = async () => {
    if (!project?.id || isUseForEditingLoading) return;
    setShowUseForEditingConfirmModal(false);
    setIsUseForEditingLoading(true);
    try {
      await projectService.setTranscriptionUseForEditing(project.id, editableColumnSource);
      if (onRefresh) await onRefresh();
      setEditableColumnChoosingOther(false);
    } catch (err) {
      logger.error('Error setting use for editing:', err);
      alert(err?.response?.data?.detail || 'Failed to set use for editing. Try again.');
    } finally {
      setIsUseForEditingLoading(false);
    }
  };

  const hasSelectedForEdit = !!finalEditableTranscript;
  const dropdownEnabled = !hasSelectedForEdit || editableColumnChoosingOther;
  const showSelectOtherButton = hasSelectedForEdit && !editableColumnChoosingOther;

  const editableColumnPreviewTranscript = editableColumnSource === 'whisper' ? whisperTranscript : googleSttTranscript;
  const editableTranscriptWithSegments = useMemo(() => {
    if (!finalEditableTranscript) return null;
    const base = finalEditableTranscript;
    if (Array.isArray(base.transcript_segments) && base.transcript_segments.length > 0) return base;
    const segs = editableColumnPreviewTranscript?.transcript_segments;
    if (Array.isArray(segs) && segs.length > 0) return { ...base, transcript_segments: segs };
    return base;
  }, [finalEditableTranscript, editableColumnPreviewTranscript]);

  const subtitleSegments = editableTranscriptWithSegments?.transcript_segments ?? segmentsForEditor;

  const handleDeliveredDownload = useCallback(() => {
    const segments = editableTranscriptWithSegments?.transcript_segments;
    const text = editableTranscriptWithSegments?.transcript_text ?? editableTranscriptWithSegments?.text ?? '';
    let content = '';
    let filename = 'transcript';
    let mime = 'text/plain';
    if (deliveredExportFormat === 'vtt') {
      content = Array.isArray(segments) && segments.length > 0 ? segmentsToVtt(segments) : `WEBVTT\n\n1\n00:00:00.000 --> 00:00:00.000\n${text}`;
      filename += '.vtt';
      mime = 'text/vtt';
    } else if (deliveredExportFormat === 'srt') {
      content = Array.isArray(segments) && segments.length > 0 ? segmentsToSrt(segments) : `1\n00:00:00,000 --> 00:00:00,000\n${text}`;
      filename += '.srt';
      mime = 'text/plain';
    } else {
      content = JSON.stringify(
        Array.isArray(segments) && segments.length > 0
          ? { transcript_text: text, transcript_segments: segments }
          : { transcript_text: text },
        null,
        2
      );
      filename += '.json';
      mime = 'application/json';
    }
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    if (project?.id) projectService.recordTranscriptDownload(project.id, deliveredExportFormat);
    if (onRefresh) onRefresh();
  }, [deliveredExportFormat, editableTranscriptWithSegments, project?.id, onRefresh]);

  const currentSubtitleSegment = useMemo(() => {
    if (!subtitleSegments || !Array.isArray(subtitleSegments) || typeof videoCurrentTime !== 'number') return null;
    const t = videoCurrentTime;
    return subtitleSegments.find((seg) => t >= Number(seg.start) && t <= Number(seg.end)) ?? null;
  }, [subtitleSegments, videoCurrentTime]);

  return (
    <>
    <div className="flex flex-col bg-background">
      <div className="bg-card border-b border-border shadow-subtle-sm flex-shrink-0 min-h-12">
        <div className="px-4 py-2 relative">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center space-x-2 min-w-0 flex-1">
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-muted rounded-md transition-colors shrink-0"
                title="Back to projects"
              >
                <ChevronRight className="w-5 h-5 text-muted-foreground rotate-180" />
              </button>
              <div className="min-w-0 max-w-[50%]">
                <h1 className="text-foreground text-sm font-semibold truncate" title={project?.title || project?.name || 'Transcription Project'}>
                  {project?.title || project?.name || 'Transcription Project'}
                </h1>
                <p className="text-[10px] text-muted-foreground truncate">
                  Workflow: {workflowStatusDisplayName}
                </p>
              </div>
            </div>

            {workflowSteps.length > 0 && (
              <div className="flex items-end gap-0 flex-wrap justify-center flex-none absolute left-1/2 -translate-x-1/2">
                <TooltipProvider delayDuration={0}>
                  {workflowSteps.map((step, i) => {
                    const roleLabel = step.role === 'contribution' ? 'Contribution' : 'Review';
                    const name = step.assignee_display_name || step.assignee_email || '—';
                    const initial = getInitial(name);
                    const stepStatus = (step.step_status || 'unassigned').replace(/_/g, ' ');
                    const isStepInProgress = (step.step_status || '').toLowerCase() === 'in_progress';
                    return (
                      <React.Fragment key={step.id ?? i}>
                        {i > 0 && <div className="w-4 h-px bg-border shrink-0 self-center mb-4" aria-hidden />}
                        <Tooltip
                          content={
                            <div className="relative bg-neutral-900 text-white rounded-lg py-1.5 px-2.5 -mx-2 -my-1 text-left shadow-lg border border-neutral-700">
                              <div className="text-xs font-medium">{roleLabel}</div>
                              <div className="text-xs text-white/90">{name}</div>
                              <div className="absolute left-1/2 -translate-x-1/2 -bottom-1.5 w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-l-transparent border-r-transparent border-t-neutral-900" aria-hidden />
                            </div>
                          }
                          side="top"
                          sideOffset={6}
                        >
                          <div className="flex flex-col items-center gap-1 shrink-0">
                            <div className="w-6 h-6 rounded-full border border-border bg-muted flex items-center justify-center text-xs font-medium text-foreground shrink-0">
                              {initial}
                            </div>
                            <span className={`text-[10px] font-medium capitalize ${isStepInProgress ? 'text-sky-600 dark:text-sky-400' : 'text-muted-foreground'}`}>
                              {stepStatus}
                            </span>
                          </div>
                        </Tooltip>
                      </React.Fragment>
                    );
                  })}
                  <>
                      <div className="w-4 h-px bg-border shrink-0 self-center mb-4" aria-hidden />
                      <Tooltip
                        content={
                          <div className="relative bg-neutral-900 text-white rounded-lg py-1.5 px-2.5 -mx-2 -my-1 text-left shadow-lg border border-neutral-700">
                            <div className="text-xs font-medium">PM Review</div>
                            <div className="text-xs text-white/90">{pmDisplayName}</div>
                            <div className="absolute left-1/2 -translate-x-1/2 -bottom-1.5 w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-l-transparent border-r-transparent border-t-neutral-900" aria-hidden />
                          </div>
                        }
                        side="top"
                        sideOffset={6}
                      >
                        <div className="flex flex-col items-center gap-1 shrink-0">
                          <div className="w-6 h-6 rounded-full border border-border bg-muted flex items-center justify-center text-xs font-medium text-foreground shrink-0">
                            {getInitial(pmDisplayName)}
                          </div>
                          <span className={`text-[10px] font-medium capitalize ${workflowStatus === 'ready_for_pm_review' ? 'text-sky-600 dark:text-sky-400' : 'text-muted-foreground'}`}>
                            {pmStepStatusLabel}
                          </span>
                        </div>
                      </Tooltip>
                    </>
                </TooltipProvider>
              </div>
            )}

            <div className="flex items-center space-x-1.5 flex-1 justify-end">
              {showAcceptButton && (
                <button type="button" onClick={handleAccept} disabled={accepting} className="bg-primary hover:bg-primary/90 text-background px-2.5 py-1.5 rounded-md text-xs font-medium flex items-center disabled:opacity-50">{accepting ? 'Accepting…' : 'Accept Assignment'}</button>
              )}
              {showSubmitButton && (
                <button type="button" onClick={handleSubmit} disabled={submitting} className="bg-primary hover:bg-primary/90 text-background px-2.5 py-1.5 rounded-md text-xs font-medium flex items-center disabled:opacity-50">{submitting ? 'Submitting…' : 'Submit'}</button>
              )}
              {showApproveButton && (
                <button type="button" onClick={handleApprove} disabled={approving} className="bg-primary hover:bg-primary/90 text-background px-2.5 py-1.5 rounded-md text-xs font-medium flex items-center disabled:opacity-50">{approving ? 'Approving…' : 'Approve'}</button>
              )}
              {showRejectButton && (
                <button type="button" onClick={handleReject} disabled={rejecting} className="bg-destructive text-destructive-foreground border border-border px-2.5 py-1.5 rounded-md text-xs font-medium flex items-center disabled:opacity-50">{rejecting ? 'Rejecting…' : 'Reject'}</button>
              )}
              {showDeliverButton && (
                <button type="button" onClick={handleDeliver} disabled={delivering} className="bg-primary hover:bg-primary/90 text-background px-2.5 py-1.5 rounded-md text-xs font-medium flex items-center disabled:opacity-50">{delivering ? 'Sending…' : 'Deliver to Creator'}</button>
              )}
              {canSetWorkflow && !isDelivered && (
                <button type="button" onClick={() => setWorkflowModalOpen(true)} className="border border-border bg-card text-card-foreground hover:bg-muted px-2.5 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5">
                  <Workflow className="w-3.5 h-3.5" /> Set Workflow
                </button>
              )}
              {canApproveReject && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" className="p-1.5 hover:bg-muted rounded-md transition-colors" aria-label="More options">
                      <MoreVertical className="w-5 h-5 text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="min-w-[8rem]">
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive focus:bg-destructive/10 disabled:opacity-50"
                      disabled={!canDeleteProject}
                      onSelect={() => { if (canDeleteProject && window.confirm('Are you sure you want to delete this project?')) onDelete(project.id); }}
                    >
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </div>
      </div>

      <SetWorkflowModal
        isOpen={workflowModalOpen}
        onClose={() => setWorkflowModalOpen(false)}
        projectId={project?.id}
        projectType={project?.projectType ?? project?.project_type}
        isLocked={isDelivered}
        onSaved={() => { onRefresh?.(); fetchWorkflowSteps(); }}
      />

      {showAcceptButton && (
        <div className="mx-2 mt-2 rounded-lg border border-amber-500/60 bg-amber-500/10 px-4 py-3 flex flex-wrap items-center gap-3">
          <span className="text-amber-800 dark:text-amber-200 font-medium">
            Accept the assignment first to start your work. You will not be able to edit the transcription until you accept.
          </span>
          <button type="button" onClick={handleAccept} disabled={accepting} className="bg-primary hover:bg-primary/90 text-background px-3 py-1.5 rounded-md text-sm font-medium flex items-center disabled:opacity-50 shrink-0">
            {accepting ? 'Accepting…' : 'Accept assignment'}
          </button>
        </div>
      )}

      <div className="flex flex-col overflow-y-auto">
        <div className="p-2 pb-8 flex flex-col gap-2">
            <div className="flex-shrink-0">
              <YoutubeLinkCard metadata={metadata} />
              {playableMediaType === 'youtube' && (
                <div className="rounded-sm border border-border bg-card p-2 text-sm text-muted-foreground mt-2">
                  Timestamp editing is available for uploaded media. This project uses a YouTube source; use the transcript cards above for text editing.
                </div>
              )}
            </div>
            <div className="grid gap-2 grid-cols-[minmax(200px,1fr)_1fr_1fr_1fr]">
              <div className="flex flex-col gap-2">
                {playableMediaType === 'gcs' && playableMediaUrl && vttBlobUrl && segmentsForEditor && (
                  <div className="flex flex-col">
                    <VideoAndTimestampEditor
                      ref={videoEditorRef}
                      videoUrl={playableMediaUrl}
                      vttBlobUrl={vttBlobUrl}
                      onTimeUpdate={setVideoCurrentTime}
                    />
                    <div className="rounded-b-sm px-2 py-2 min-h-[2rem] flex items-center justify-center text-center text-xs text-background bg-black/80">
                      {currentSubtitleSegment?.text != null && currentSubtitleSegment.text !== '' ? (
                        <span>{currentSubtitleSegment.text}</span>
                      ) : (
                        <span className="text-muted-foreground">&nbsp;</span>
                      )}
                    </div>
                  </div>
                )}
                <div className="rounded-sm border border-border bg-card shadow-subtle-sm overflow-hidden">
                  <OverviewCard project={{ ...project, ...details?.project }} metadata={metadata} transcript={transcript} currentSynopsis={keywords} />
                </div>
                <div className="rounded-sm border border-border bg-card shadow-subtle-sm overflow-hidden">
                  <LeaveNoteCard projectId={project?.id} initialNote={project?.notes || details?.notes || ''} />
                </div>
              </div>
              <div className="flex flex-col rounded-sm border border-border bg-card shadow-subtle-sm overflow-hidden min-h-0">
                <h2 className="text-foreground text-sm font-semibold px-2 py-1.5 border-b border-border flex-shrink-0 flex items-center gap-2 flex-wrap">
                  Google STT Transcription
                  {googleSttTranscript?.language_code && (
                    <span className="text-muted-foreground font-normal text-xs">
                      {String(googleSttTranscript.language_code).toUpperCase()}
                    </span>
                  )}
                  </h2>
                <div className="max-h-[90vh] overflow-y-auto flex flex-col px-1">
                  <TranscriptionReadOnlyBox
                    transcript={googleSttTranscript}
                    title=""
                    segmentMissingInOther={googleMissingInWhisper.length > 0 ? googleMissingInWhisper : null}
                    missingLabel="Missing in Whisper"
                    videoCurrentTime={hasVideoSync ? videoCurrentTime : undefined}
                    onSegmentSeek={hasVideoSync ? handleSegmentSeek : undefined}
                    embedded
                  />
                </div>
              </div>
              <div className="flex flex-col rounded-sm border border-border bg-card shadow-subtle-sm overflow-hidden min-h-0">
                <h2 className="text-foreground text-sm font-semibold px-2 py-1.5 border-b border-border flex-shrink-0 flex items-center gap-2 flex-wrap">
                  Whisper Transcription
                  {whisperTranscript?.language_code && (
                    <span className="text-muted-foreground font-normal text-xs">
                      {String(whisperTranscript.language_code).toUpperCase()}
                    </span>
                  )}
                  </h2>
                <div className="max-h-[90vh] overflow-y-auto flex flex-col px-1">
                  <TranscriptionReadOnlyBox
                    transcript={whisperTranscript}
                    title=""
                    segmentMissingInOther={whisperMissingInGoogle.length > 0 ? whisperMissingInGoogle : null}
                    missingLabel="Missing in Google STT"
                    videoCurrentTime={hasVideoSync ? videoCurrentTime : undefined}
                    onSegmentSeek={hasVideoSync ? handleSegmentSeek : undefined}
                    embedded
                  />
                </div>
                {!whisperTranscript && (
                  <p className="text-muted-foreground text-xs px-2 pb-2 flex-shrink-0">Whisper transcript not yet available.</p>
                )}
              </div>
              <div className="flex flex-col gap-2 min-h-0">
                {isDelivered && (transcriptDownloadCount > 0 || lastTranscriptDownloadedAt) && (
                  <div className="rounded-sm border border-border bg-card shadow-subtle-sm px-2 py-1.5 flex-shrink-0">
                    <h2 className="text-foreground text-sm font-semibold mb-1">Creator downloads</h2>
                    <p className="text-xs text-muted-foreground">
                      Transcript downloaded {transcriptDownloadCount} time{transcriptDownloadCount !== 1 ? 's' : ''}.
                      {lastTranscriptDownloadedAt && (
                        <> Last downloaded at {new Date(lastTranscriptDownloadedAt).toLocaleString()}.</>
                      )}
                    </p>
                  </div>
                )}
                <div className="flex flex-col rounded-sm border border-border bg-card shadow-subtle-sm overflow-hidden min-h-0">
                <h2 className="text-foreground text-sm font-semibold px-2 py-1.5 border-b border-border flex-shrink-0">
                  Editable transcription
                </h2>
                <div className="flex flex-shrink-0 items-center gap-2 px-2 py-1.5 border-b border-border flex-wrap">
                  {isDelivered ? (
                    <>
                      <div className="relative editable-column-source-dropdown">
                        <select
                          value={deliveredExportFormat}
                          onChange={(e) => setDeliveredExportFormat(e.target.value)}
                          className="min-w-[100px] border border-border px-2.5 py-1.5 rounded-lg text-xs font-medium bg-card text-card-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        >
                          <option value="vtt">VTT</option>
                          <option value="srt">SRT</option>
                          <option value="json">json file</option>
                        </select>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="default"
                        rounded="lg"
                        onClick={handleDeliveredDownload}
                      >
                        Download
                      </Button>
                    </>
                  ) : canFinalize(user) && canEditEditableTranscript ? (
                    <>
                      <div className="relative editable-column-source-dropdown">
                        <button
                          type="button"
                          onClick={() => dropdownEnabled && setShowEditableSourceDropdown(!showEditableSourceDropdown)}
                          disabled={!dropdownEnabled}
                          className="min-w-[100px] border border-border px-2.5 py-1.5 rounded-lg text-xs font-medium flex items-center justify-between bg-card text-card-foreground hover:bg-muted focus:outline-none disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                          <span>{editableColumnSource === 'whisper' ? 'Whisper' : 'Google STT'}</span>
                          <ChevronDown className="w-3.5 h-3.5 ml-0.5 shrink-0" />
                        </button>
                        {showEditableSourceDropdown && dropdownEnabled && (
                          <div className="absolute left-0 mt-2 w-44 bg-popover border border-border rounded-xl shadow-subtle-sm z-50 py-1">
                            <button type="button" onClick={() => { setEditableColumnSource('google_stt'); setShowEditableSourceDropdown(false); }} className="w-full text-left px-4 py-2 text-sm text-popover-foreground hover:bg-muted">Google STT</button>
                            <button type="button" onClick={() => { setEditableColumnSource('whisper'); setShowEditableSourceDropdown(false); }} className="w-full text-left px-4 py-2 text-sm text-popover-foreground hover:bg-muted">Whisper</button>
                          </div>
                        )}
                      </div>
                      {showSelectOtherButton ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="default"
                          rounded="lg"
                          onClick={() => { setEditableColumnChoosingOther(true); setShowEditableSourceDropdown(true); }}
                          disabled={false}
                        >
                          Select other
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          variant="default"
                          rounded="lg"
                          onClick={handleUseForEditing}
                          disabled={isUseForEditingLoading || (editableColumnSource === 'whisper' && !whisperTranscript)}
                        >
                          {isUseForEditingLoading ? 'Saving…' : 'Use for editing'}
                        </Button>
                      )}
                    </>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      {showAcceptButton
                        ? 'Accept the assignment above to unlock editing.'
                        : canFinalize(user) && !canEditEditableTranscript
                          ? 'Another assignee is currently editing. Source: ' + (editableColumnSource === 'whisper' ? 'Whisper' : 'Google STT') + '. You can view the content.'
                          : `Source: ${editableColumnSource === 'whisper' ? 'Whisper' : 'Google STT'}. Only a reviewer or above can set the source for the editable transcript.`}
                    </span>
                  )}
                </div>
                <div className="max-h-[90vh] overflow-y-auto flex flex-col">
                  {!finalEditableTranscript ? (
                    <div className="flex flex-col items-center justify-center p-6 text-center min-h-[120px] bg-muted/20 rounded-b-lg border-t-0 border-border">
                      <p className="text-sm text-muted-foreground max-w-sm">
                        {isDelivered
                          ? 'No transcript to display.'
                          : showAcceptButton
                            ? 'Accept the assignment above to start your work. After accepting, you can select a source and use it for editing.'
                            : <>No editable transcript yet. Select a model (Google STT or Whisper) above and click <strong>Use for editing</strong> to load the transcript for editing.</>}
                      </p>
                    </div>
                  ) : (
                    <EditableTranscriptBox
                      transcript={editableTranscriptWithSegments}
                      projectId={project?.id}
                      onSaved={onRefresh}
                      title="Editable transcription"
                      disabled={!finalEditableTranscript}
                      source="final_editable"
                      embedded
                      readOnly={isDelivered || !canEditEditableTranscript}
                      videoCurrentTime={hasVideoSync ? videoCurrentTime : undefined}
                      onSegmentSeek={hasVideoSync ? handleSegmentSeek : undefined}
                    />
                  )}
                </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AlertDialog open={showUseForEditingConfirmModal} onOpenChange={setShowUseForEditingConfirmModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Overwrite editable transcription?</AlertDialogTitle>
            <AlertDialogDescription>
              This project already has an editable transcription. Choosing a different source (Google STT or Whisper) will replace it. Are you sure you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowUseForEditingConfirmModal(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={applyUseForEditing}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default TranscriptionProjectDetailPage;
